/**
 * useRevenueEntries Hook
 * Custom hook for fetching, creating, updating, and deleting revenue entries using TanStack Query.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { revenueEntriesApi, type RevenueEntryRecord } from '../api/transactionApi';
import { useUIStore } from '../store/uiStore';
import { monthlyRevenueKeys } from './useMonthlyRevenue';
import { reportKeys } from './useYearlyReport';
import { type SupportedCurrency, calculateTotalInCurrency } from '../types';

export const revenueEntriesKeys = {
  all: ['revenueEntries'] as const,
  month: (year: number, month: number) => [...revenueEntriesKeys.all, year, month] as const,
  summary: () => [...revenueEntriesKeys.all, 'summary'] as const,
};

export type { RevenueEntryRecord };

/**
 * Hook for fetching revenue entries for a specific month
 * @param year Year
 * @param month Month (1-12)
 * @param targetCurrency Optional target currency for total calculation (default: BRL)
 */
export function useRevenueEntries(
  year: number,
  month: number,
  targetCurrency: SupportedCurrency = 'BRL'
) {
  const query = useQuery({
    queryKey: revenueEntriesKeys.month(year, month),
    queryFn: () => revenueEntriesApi.getEntries(year, month),
    staleTime: 1 * 60 * 1000, // 1 minute
    retry: false,
  });

  const entries = query.data?.data?.entries ?? [];

  // Calculate total in the target currency using stored currency snapshots from each entry
  // Formula: DisplayValue = amountBRL * currencySnapshots[targetCurrency]
  const total = calculateTotalInCurrency(entries, targetCurrency);

  return {
    data: query.data?.data ?? null,
    entries,
    total,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook for creating a new revenue entry
 */
export function useCreateRevenueEntry() {
  const queryClient = useQueryClient();
  const { addToast } = useUIStore();

  return useMutation({
    mutationFn: ({ amount, description, date, currency }: { amount: number; description: string; date: string; currency?: string }) =>
      revenueEntriesApi.createEntry(amount, description, date, currency),
    onSuccess: () => {
      // Invalidate revenue entries, monthly revenue, and report queries
      queryClient.invalidateQueries({ queryKey: revenueEntriesKeys.all });
      queryClient.invalidateQueries({ queryKey: monthlyRevenueKeys.all });
      queryClient.invalidateQueries({ queryKey: reportKeys.all });
      addToast('success', 'Receita adicionada com sucesso!');
    },
    onError: (error: Error) => {
      addToast('error', error.message || 'Erro ao adicionar receita');
    },
  });
}

/**
 * Hook for fetching revenue summary (current month total and 6-month average)
 */
export function useRevenueSummary() {
  const query = useQuery({
    queryKey: revenueEntriesKeys.summary(),
    queryFn: () => revenueEntriesApi.getSummary(),
    staleTime: 1 * 60 * 1000, // 1 minute
    retry: false,
  });

  return {
    data: query.data?.data ?? null,
    currentMonthTotal: query.data?.data?.currentMonthTotal ?? 0,
    rollingAverage: query.data?.data?.rollingAverage ?? 0,
    monthsWithData: query.data?.data?.monthsWithData ?? 0,
    monthlyBreakdown: query.data?.data?.monthlyBreakdown ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook for updating a revenue entry
 */
export function useUpdateRevenueEntry() {
  const queryClient = useQueryClient();
  const { addToast } = useUIStore();

  return useMutation({
    mutationFn: ({
      entryId,
      data,
    }: {
      entryId: string;
      data: { amount?: number; description?: string; date?: string };
    }) => revenueEntriesApi.updateEntry(entryId, data),
    onSuccess: () => {
      // Invalidate all related queries for global state sync
      queryClient.invalidateQueries({ queryKey: revenueEntriesKeys.all });
      queryClient.invalidateQueries({ queryKey: monthlyRevenueKeys.all });
      queryClient.invalidateQueries({ queryKey: reportKeys.all });
      addToast('success', 'Receita atualizada com sucesso!');
    },
    onError: (error: Error) => {
      addToast('error', error.message || 'Erro ao atualizar receita');
    },
  });
}

/**
 * Hook for deleting a revenue entry
 */
export function useDeleteRevenueEntry() {
  const queryClient = useQueryClient();
  const { addToast } = useUIStore();

  return useMutation({
    mutationFn: (entryId: string) => revenueEntriesApi.deleteEntry(entryId),
    onSuccess: () => {
      // Invalidate all related queries for global state sync
      queryClient.invalidateQueries({ queryKey: revenueEntriesKeys.all });
      queryClient.invalidateQueries({ queryKey: monthlyRevenueKeys.all });
      queryClient.invalidateQueries({ queryKey: reportKeys.all });
      addToast('success', 'Receita excluída com sucesso!');
    },
    onError: (error: Error) => {
      addToast('error', error.message || 'Erro ao excluir receita');
    },
  });
}
